import {
  AlertTriangle,
  BadgeDollarSign,
  ArrowDownRight,
  ArrowUpRight,
  Bell,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Coins,
  Clock3,
  FileCheck2,
  FileText,
  Grid2X2,
  Home,
  Inbox,
  LoaderCircle,
  MessageSquareText,
  MoreHorizontal,
  Plus,
  Search,
  Settings2,
  ShieldCheck,
  Table2,
  UserRoundSearch,
  ContactRound,
  Users,
  Video,
  WalletCards,
  ReceiptText,
  LifeBuoy,
  KeyRound,
  Mail,
  Megaphone,
  Salad,
  Smartphone,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useRef, type FormEvent, type ReactNode } from 'react';

let activeModalScrollLocks = 0;
let modalScrollPosition = { x: 0, y: 0 };
let modalBodyStyleSnapshot: Partial<CSSStyleDeclaration> | null = null;

function lockDocumentForModal() {
  activeModalScrollLocks += 1;
  if (activeModalScrollLocks > 1) return;
  modalScrollPosition = { x: window.scrollX, y: window.scrollY };
  modalBodyStyleSnapshot = {
    position: document.body.style.position,
    top: document.body.style.top,
    right: document.body.style.right,
    bottom: document.body.style.bottom,
    left: document.body.style.left,
    width: document.body.style.width,
    overflow: document.body.style.overflow,
    touchAction: document.body.style.touchAction,
  };
  document.documentElement.classList.add('modal-open');
  document.body.classList.add('modal-open');
  Object.assign(document.body.style, {
    position: 'fixed',
    top: `-${modalScrollPosition.y}px`,
    right: '0',
    bottom: '0',
    left: `-${modalScrollPosition.x}px`,
    width: '100%',
    overflow: 'hidden',
    touchAction: 'pan-y',
  });
}

function unlockDocumentForModal() {
  activeModalScrollLocks = Math.max(0, activeModalScrollLocks - 1);
  if (activeModalScrollLocks > 0) return;
  document.documentElement.classList.remove('modal-open');
  document.body.classList.remove('modal-open');
  if (modalBodyStyleSnapshot) Object.assign(document.body.style, modalBodyStyleSnapshot);
  modalBodyStyleSnapshot = null;
  window.scrollTo(modalScrollPosition.x, modalScrollPosition.y);
}

export function useModalScrollLock(open: boolean) {
  useEffect(() => {
    if (!open) return;
    lockDocumentForModal();
    return unlockDocumentForModal;
  }, [open]);
}

export const iconMap: Record<string, LucideIcon> = {
  home: Home,
  briefcase: BriefcaseBusiness,
  tasks: Table2,
  people: Users,
  profile: ContactRound,
  users: Users,
  finance: WalletCards,
  'currency-finance': Coins,
  wallet: WalletCards,
  grid: Grid2X2,
  attendance: Clock3,
  'device-activity': Smartphone,
  schedule: CalendarDays,
  clock: Clock3,
  leave: CalendarDays,
  payroll: BadgeDollarSign,
  approvals: CheckCircle2,
  'check-circle': CheckCircle2,
  support: LifeBuoy,
  chat: MessageSquareText,
  messages: MessageSquareText,
  meetings: Video,
  notifications: Bell,
  announcements: Megaphone,
  crm: UserRoundSearch,
  settings: Settings2,
  audit: ShieldCheck,
  vault: KeyRound,
  email: Mail,
  policies: FileCheck2,
  documents: FileText,
  reimbursements: ReceiptText,
  feedback: MessageSquareText,
  meals: Salad,
  more: Grid2X2,
};

export function BrandLogo({ surface = 'light', className = '' }: { surface?: 'light' | 'dark'; className?: string }) {
  return <img className={`brand-logo ${className}`.trim()}
    src={surface === 'dark' ? '/brand/nosivo-wordmark-on-dark.png?v=20260816-green-1' : '/brand/nosivo-wordmark-on-light.png?v=20260816-green-1'}
    alt="Nosivo" decoding="async" />;
}

export function BrandIcon({ className = '' }: { className?: string }) {
  return <img className={`brand-icon ${className}`.trim()} src="/brand/nosivo-icon-192.png?v=20260816-green-1" alt="Nosivo" decoding="async" />;
}

const DEFAULT_AVATAR_COUNT = 30;

export function defaultAvatarUrl(name: string) {
  const hash = [...String(name || 'Nosivo')].reduce((total, character) => (
    Math.imul(total ^ (character.codePointAt(0) || 0), 16777619) >>> 0
  ), 2166136261);
  const number = String((hash % DEFAULT_AVATAR_COUNT) + 1).padStart(2, '0');
  return `/avatars/defaults/avatar-${number}.webp`;
}

export function Avatar({ name, color, imageUrl, size = 'md' }: { name: string; color?: string | null; imageUrl?: string | null; size?: 'sm' | 'md' | 'lg' }) {
  return <span className={`avatar avatar--${size}`} style={{ background: color || '#edf0f7' }} role="img" aria-label={`${name} profile picture`}>
    <img className="avatar__default" src={defaultAvatarUrl(name)} alt="" aria-hidden="true" decoding="async" onError={(event) => event.currentTarget.remove()} />
    {imageUrl && <img className="avatar__uploaded" src={imageUrl} alt="" decoding="async" onError={(event) => event.currentTarget.remove()} />}
  </span>;
}

export function PageHeader({ eyebrow, title, description, actions }: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return <header className="page-header">
    <div>
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h1>{title}</h1>
      {description && <p>{description}</p>}
    </div>
    {actions && <div className="page-header__actions">{actions}</div>}
  </header>;
}

export function Button({ children, variant = 'primary', size = 'md', icon: Icon, busy, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  icon?: LucideIcon;
  busy?: boolean;
}) {
  return <button className={`button button--${variant} button--${size}`} disabled={busy || props.disabled} {...props}>
    {busy ? <LoaderCircle className="spin" size={16} /> : Icon ? <Icon size={16} /> : null}
    <span>{children}</span>
  </button>;
}

export function IconButton({ label, children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }) {
  return <button className="icon-button" aria-label={label} title={label} {...props}>{children}</button>;
}

export function Status({ value }: { value?: string | null }) {
  const normalized = String(value || 'unknown').toLowerCase().replace(/\s+/g, '_');
  return <span className={`status status--${normalized}`}><span />{String(value || 'Unknown').replace(/_/g, ' ')}</span>;
}

export function Priority({ value }: { value?: string | null }) {
  const normalized = String(value || 'medium').toLowerCase();
  return <span className={`priority priority--${normalized}`}>{normalized}</span>;
}

export function MetricCard({ label, value, hint, tone = 'neutral', trend }: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'neutral' | 'green' | 'blue' | 'amber' | 'red';
  trend?: 'up' | 'down';
}) {
  return <article className={`metric-card metric-card--${tone}`}>
    <div className="metric-card__label">{label}</div>
    <div className="metric-card__value">{value}</div>
    {hint && <div className="metric-card__hint">
      {trend === 'up' ? <ArrowUpRight size={14} /> : trend === 'down' ? <ArrowDownRight size={14} /> : null}{hint}
    </div>}
  </article>;
}

export function Progress({ value, label }: { value: number; label?: string }) {
  const safe = Math.min(100, Math.max(0, Number(value || 0)));
  return <div className="progress-wrap">
    <div className="progress-track"><span style={{ width: `${safe}%` }} /></div>
    {label && <small>{label}</small>}
  </div>;
}

export function EmptyState({ icon: Icon = Inbox, title, description, action }: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return <div className="empty-state"><span className="empty-state__icon"><Icon size={22} /></span><h3>{title}</h3>{description && <p>{description}</p>}{action}</div>;
}

export function Loading({ label = 'Loading workspace' }: { label?: string }) {
  return <div className="loading-state"><LoaderCircle className="spin" /><span>{label}</span></div>;
}

export function BrandedLoading({ label = 'Opening your workspace' }: { label?: string }) {
  return <div className="brand-loading" role="status" aria-live="polite">
    <BrandLogo surface="light" className="brand-loading__logo" />
    <span className="brand-loading__track" aria-hidden="true"><span /></span>
    <small>{label}</small>
  </div>;
}

export function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  return <div className="error-state"><AlertTriangle size={20} /><div><strong>Something needs attention</strong><p>{message}</p></div>{retry && <Button variant="secondary" size="sm" onClick={retry}>Try again</Button>}</div>;
}

export function Modal({ open, title, description, onClose, children, footer, width = 'md', dismissible = true }: {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
  dismissible?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalScrollLock(open);
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => { if (dismissible && event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLElement>('input,select,textarea,button')?.focus());
    return () => { document.removeEventListener('keydown', handler); };
  }, [dismissible, open, onClose]);
  if (!open) return null;
  return <div className="modal" role="presentation" onMouseDown={(event) => { if (dismissible && event.target === event.currentTarget) onClose(); }}>
    <div className={`modal__dialog modal__dialog--${width}`} role="dialog" aria-modal="true" aria-labelledby="modal-title" ref={dialogRef}>
      <header className="modal__header"><div><h2 id="modal-title">{title}</h2>{description && <p>{description}</p>}</div>{dismissible && <IconButton label="Close" onClick={onClose}><X size={18} /></IconButton>}</header>
      <div className="modal__body">{children}</div>
      {footer && <footer className="modal__footer">{footer}</footer>}
    </div>
  </div>;
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return <label className={`field ${error ? 'field--error' : ''}`}><span className="field__label">{label}</span>{children}{hint && <small>{hint}</small>}{error && <small className="field__error">{error}</small>}</label>;
}

export function FormGrid({ children, columns = 2 }: { children: ReactNode; columns?: 1 | 2 | 3 }) {
  return <div className={`form-grid form-grid--${columns}`}>{children}</div>;
}

export function Tabs({ items, active, onChange }: { items: Array<{ key: string; label: string; count?: number }>; active: string; onChange: (key: string) => void }) {
  return <div className="tabs" role="tablist">{items.map((item) => <button key={item.key} role="tab" aria-selected={active === item.key} className={active === item.key ? 'active' : ''} onClick={() => onChange(item.key)}>{item.label}{item.count !== undefined && <span>{item.count}</span>}</button>)}</div>;
}

export function SearchInput({ value, onChange, placeholder = 'Search', compact = false }: { value: string; onChange: (value: string) => void; placeholder?: string; compact?: boolean }) {
  return <label className={`search-input ${compact ? 'search-input--compact' : ''}`}><Search size={16} /><input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} /></label>;
}

export function SectionHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return <div className="section-header"><div><h2>{title}</h2>{description && <p>{description}</p>}</div>{action}</div>;
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function formatDate(value?: string | null, includeTime = false) {
  if (!value) return '—';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-IN', includeTime ? { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' } : { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

export function formatMoney(value: number | string, currency = 'INR') {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(value || 0));
}

export { Bell, CalendarDays, Check, ChevronDown, CircleDollarSign, Clock3, Grid2X2, MoreHorizontal, Plus, Search, Settings2, Users, WalletCards, X };
